import React, { useState, useMemo, useCallback } from 'react';
import { LOGO_BASE64 } from '../data/logo';
import { db } from '../data/store';
import { MAPI_URL, SERVER_URL } from '../data/serverStore';
import { getBillingContact, getClientProfile, getClientProfiles, saveClientProfile } from '../data/clientProfiles';

// Calculate sample price from pricing setup
function calcSamplePrice(sample, pricing) {
  if (!sample || !pricing) return 0;
  // If sample already has a manually set price, use it
  if (sample.price && sample.price > 0) return sample.price;
  const params = sample.params || [];
  // Sum per-parameter prices
  if (params.length > 0 && pricing.byParam && Object.keys(pricing.byParam).length > 0) {
    const paramTotal = params.reduce((sum, p) => sum + (pricing.byParam[p] || 0), 0);
    if (paramTotal > 0) {
      // Apply rush multiplier
      const rush = sample.rush ? (pricing.rushMultipliers?.[sample.rush] || 1) : 1;
      return paramTotal * rush;
    }
  }
  // Fall back to category price
  const catPrice = pricing.byCategory?.[sample.testType] || pricing.basePrice || 0;
  const rush = sample.rush ? (pricing.rushMultipliers?.[sample.rush] || 1) : 1;
  return catPrice * rush;
}

const fmt = (n) => `$${(n||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}`;
const canonicalize = value => {
  if (Array.isArray(value)) return value.map(canonicalize);
  if (value && typeof value === 'object') return Object.keys(value).sort().reduce((result, key) => {
    result[key] = canonicalize(value[key]);
    return result;
  }, {});
  return value;
};
const sameData = (left, right) => JSON.stringify(canonicalize(left)) === JSON.stringify(canonicalize(right));
const htmlEscape = value => String(value ?? '')
  .replaceAll('&', '&amp;')
  .replaceAll('<', '&lt;')
  .replaceAll('>', '&gt;')
  .replaceAll('"', '&quot;')
  .replaceAll("'", '&#039;');
const shortDate = value => {
  if (!value) return '';
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${match[2]}/${match[3]}/${match[1].slice(2)}`;
  return htmlEscape(value);
};
const defaultDueDate = value => {
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return '';
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  date.setDate(date.getDate() + 30);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};
const todayStr = () => new Date().toISOString().split('T')[0];
const daysSince = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / 86400000);
};
// An outstanding invoice is "due for a reminder" once 30+ days have passed
// since whichever came last — the original send, or the most recent
// reminder. Never true before it's actually been sent once.
const REMINDER_INTERVAL_DAYS = 30;
const needsReminder = (inv) => {
  if (inv.status !== 'Outstanding' || !inv.dateSent) return false;
  const lastContact = inv.reminders?.length
    ? inv.reminders[inv.reminders.length - 1].date
    : inv.dateSent;
  const d = daysSince(lastContact);
  return d !== null && d >= REMINDER_INTERVAL_DAYS;
};

// ─────────────────────────────────────────────────────────────────────────────
// OLD-STYLE INVOICE DOCUMENT
// ─────────────────────────────────────────────────────────────────────────────
function buildInvoiceDocumentHTML(inv, invSamples, getPrice, isReminder=false) {
  const subtotal = invSamples.reduce((sum, sample) => sum + getPrice(sample), 0);
  const discount = Number(inv.discount) || 0;
  const lateFee = Number(inv.lateFee) || 0;
  const invoiceTotal = Math.max(subtotal - discount, 0);
  const payThisAmount = invoiceTotal + lateFee;
  const dueDate = inv.dueDate || defaultDueDate(inv.date);
  const contact = inv.attention || inv.contact || inv.clientEmail || '';
  const terms = inv.terms || 'Net 30';
  const billingAddress = inv.billingAddress || inv.billToAddress || '';
  const denseClass = invSamples.length > 20 ? ' dense' : '';
  const invoicePO = sample =>
    (inv.samplePOs && inv.samplePOs[String(sample.id)]) ||
    (inv.woPOs && inv.woPOs[sample.workOrder]) || inv.poNumber || '';

  return `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Invoice #${htmlEscape(inv.num)}</title>
  <style>
    @page{size:A4 portrait;margin:12mm;}
    *{box-sizing:border-box;}
    html,body{margin:0;padding:0;background:#fff;color:#111;font-family:Arial,Helvetica,sans-serif;}
    body{font-size:9.5pt;}
    .invoice-page{width:calc(100% - 10mm);max-width:calc(100% - 10mm);min-height:273mm;margin-right:10mm;padding:0;display:flex;flex-direction:column;}
    .reminder{border:1px solid #a96b00;background:#fff4d6;color:#784b00;padding:6px 9px;
      font-weight:bold;margin-bottom:7px;text-align:center;}
    .top{display:grid;grid-template-columns:minmax(0,45fr) minmax(0,55fr);gap:16px;align-items:start;}
    .field-label{font-size:10pt;font-weight:700;margin:0 0 2px 1px;}
    .billing-box{height:72px;border:1.2px solid #111;display:grid;grid-template-rows:repeat(4,1fr);}
    .billing-line{border-bottom:1px solid #111;padding:2px 4px;font-size:9.5pt;overflow:hidden;}
    .billing-line:last-child{border-bottom:0;}
    .contact-row{display:grid;grid-template-columns:1fr 112px;gap:12px;margin-top:5px;align-items:end;}
    .field-box{height:24px;border:1.2px solid #111;padding:3px 5px;font-size:9.5pt;overflow:hidden;}
    .invoice-head{position:relative;min-height:145px;min-width:0;}
    .page-number{position:absolute;right:12px;top:1px;width:52px;text-align:right;font-size:10pt;white-space:nowrap;}
    .logo-wrap{text-align:center;height:68px;}
    .logo-wrap img{height:64px;max-width:210px;object-fit:contain;}
    .invoice-word{text-align:center;font-size:25pt;line-height:1;font-weight:800;margin:1px 0 4px;}
    .head-details{display:grid;grid-template-columns:minmax(0,1fr) 184px;gap:10px;align-items:end;min-width:0;}
    .invoice-total-label{text-align:center;font-size:11pt;font-weight:700;}
    .invoice-total-box{border:1.2px solid #111;text-align:center;font-size:15pt;font-weight:800;padding:4px 7px;margin-top:2px;}
    .meta-grid{display:grid;grid-template-columns:minmax(0,1fr) 88px;gap:3px 6px;align-items:center;font-size:10pt;font-weight:700;min-width:0;}
    .meta-value{border:1.2px solid #111;padding:3px 4px;font-size:11.5pt;font-weight:400;text-align:center;white-space:nowrap;overflow:hidden;}
    .rule{border:0;border-top:1.5px solid #111;margin:6px 0 0;}
    .sample-table{width:100%;border-collapse:collapse;table-layout:fixed;}
    .sample-table th{font-size:8.6pt;line-height:1.05;padding:5px 3px 6px;text-align:center;border-bottom:1.5px solid #111;vertical-align:bottom;}
    .sample-table td{font-size:8.4pt;padding:4px 3px;vertical-align:top;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
    .sample-table td.num{text-align:right;}
    .sample-table.dense td{font-size:8pt;padding-top:3px;padding-bottom:3px;}
    .sample-table .sample{width:10%;}.sample-table .po{width:14%;}.sample-table .received{width:13%;}
    .sample-table .completed{width:13%;}.sample-table .description{width:26%;}
    .sample-table .charge{width:12%;}.sample-table .total{width:12%;}
    .totals-area{display:flex;justify-content:flex-end;align-items:flex-start;gap:16px;margin-top:5px;}
    .due{display:flex;align-items:center;gap:7px;font-size:13pt;font-weight:800;margin-top:4px;}
    .due-box{border:1.2px solid #111;padding:4px 8px;font-size:11pt;min-width:92px;text-align:center;}
    .totals-grid{display:grid;grid-template-columns:auto 112px;align-items:center;text-align:right;font-size:12pt;font-weight:800;}
    .totals-grid .amount{border:1.2px solid #111;padding:3px 7px;min-height:24px;}
    .totals-grid .pay-label{font-size:14pt;padding:7px 8px 0 0;}
    .totals-grid .pay{margin-top:7px;font-size:14pt;}
    .footer{margin-top:auto;border-top:1.5px solid #111;padding:5px 2px 0;display:grid;grid-template-columns:minmax(0,1fr) 210px;gap:14px;}
    .payment-title{font-size:9.2pt;font-weight:800;line-height:1.2;margin-bottom:2px;white-space:normal;}
    .payment-copy{font-size:9.5pt;line-height:1.35;}
    .thanks{font-size:11pt;font-weight:800;margin-top:2px;}
    .cards{display:flex;gap:3px;margin-top:5px;}
    .card{border:1px solid #777;padding:2px 4px;font-size:6.5pt;font-weight:700;background:#fff;}
    .address{font-size:9.5pt;font-weight:700;line-height:1.25;}
    @media print{.invoice-page{min-height:273mm;}.sample-table tr{break-inside:avoid;}}
  </style></head><body><main class="invoice-page">
  ${isReminder ? `<div class="reminder">PAYMENT REMINDER - This invoice remains outstanding.</div>` : ''}
  <section class="top">
    <div>
      <div class="field-label">Billing Information</div>
      <div class="billing-box">
        <div class="billing-line">${htmlEscape(inv.client)}</div>
        <div class="billing-line">${htmlEscape(billingAddress)}</div>
        <div class="billing-line">${htmlEscape(inv.billingAddress2 || '')}</div>
        <div class="billing-line">${htmlEscape(inv.notes || '')}</div>
      </div>
      <div class="contact-row">
        <div><div class="field-label">Contact</div><div class="field-box">${htmlEscape(contact)}</div></div>
        <div><div class="field-label">Terms</div><div class="field-box">${htmlEscape(terms)}</div></div>
      </div>
    </div>
    <div class="invoice-head">
      <div class="page-number">page 1</div>
      <div class="logo-wrap"><img src="${LOGO_BASE64}" alt="Sterling Analytical"/></div>
      <div class="invoice-word">INVOICE</div>
      <div class="head-details">
        <div><div class="invoice-total-label">Invoice Total</div><div class="invoice-total-box">${fmt(invoiceTotal)}</div></div>
        <div class="meta-grid">
          <div>Invoice Number</div><div class="meta-value">${htmlEscape(inv.num)}</div>
          <div>Invoice Date</div><div class="meta-value">${shortDate(inv.date)}</div>
        </div>
      </div>
    </div>
  </section>
  <hr class="rule"/>
  <table class="sample-table${denseClass}">
    <thead><tr>
      <th class="sample">Sample<br>Number</th><th class="po">PO<br>Number</th>
      <th class="received">Date<br>Received</th><th class="completed">Date<br>Completed</th><th class="description">Description</th>
      <th class="charge">Sample<br>Charge</th><th class="total">Total</th>
    </tr></thead>
    <tbody>
      ${invSamples.map(sample => {
        const price = getPrice(sample);
        return `<tr>
          <td>${htmlEscape(sample.id)}</td><td>${htmlEscape(invoicePO(sample))}</td>
          <td>${shortDate(sample.received)}</td><td>${shortDate(sample.dateCompleted)}</td><td title="${htmlEscape(sample.desc || '')}">${htmlEscape(sample.desc || '')}</td>
          <td class="num">${fmt(price)}</td><td class="num">${fmt(price)}</td>
        </tr>`;
      }).join('')}
    </tbody>
  </table>
  <section class="totals-area">
    <div class="due">Payment Due Date <span class="due-box">${shortDate(dueDate)}</span></div>
    <div class="totals-grid">
      <div>Invoice Total</div><div class="amount">${fmt(subtotal)}</div>
      ${discount > 0 ? `<div>Discount</div><div class="amount">-${fmt(discount)}</div>` : ''}
      <div>Late Fee</div><div class="amount">${fmt(lateFee)}</div>
      <div class="pay-label">Pay This Amount</div><div class="amount pay">${fmt(payThisAmount)}</div>
    </div>
  </section>
  <footer class="footer">
    <div>
      <div class="payment-title">Please Pay the Total Invoice Amount and send payment to this address.</div>
      <div class="payment-copy">Payments received after the Due Date will be subject to a Late Fee.<br>
        The Late Fee is $25 for each 30 days the invoice remains unpaid.</div>
      <div class="thanks">Thank you.</div>
      <div class="cards"><span class="card">DISCOVER</span><span class="card">MASTERCARD</span><span class="card">VISA</span><span class="card">AMEX</span></div>
    </div>
    <div class="address">Sterling Analytical, Inc.<br>15 Agawam Ave.<br>West Springfield, MA 01089<br>
      Ph 413-214-6541<br>www.sterlinganalytical.com</div>
  </footer>
  </main></body></html>`;
}

function printInvoiceHTML(inv, samples, pricing={}) {
  const invSamples = (inv.sampleIds||[]).map(id=>samples.find(s=>s.id===id)).filter(Boolean);
  const getPrice = sample => sample.price || calcSamplePrice(sample, pricing) || 0;
  const win = window.open('','_blank');
  win.document.write(buildInvoiceDocumentHTML(inv, invSamples, getPrice));
  win.document.close();
  win.onload = () => win.print();
}

// ─────────────────────────────────────────────────────────────────────────────
// BUILD INVOICE EMAIL HTML
// ─────────────────────────────────────────────────────────────────────────────
function buildInvoiceEmailHTML(inv, invSamples, getPrice, liveTotal, isReminder=false) {
  return buildInvoiceDocumentHTML(inv, invSamples, getPrice, isReminder);
}

// ─────────────────────────────────────────────────────────────────────────────
// PRICING TAB
// ─────────────────────────────────────────────────────────────────────────────
function PricingTab({ toast }) {
  const [pricing, setPricing] = useState(() => db.getPricing());
  const [activeSection, setActiveSection] = useState('category');
  const [newParam, setNewParam] = useState({ name:'', price:'' });
  const [editingParam, setEditingParam] = useState(null);
  const [search, setSearch] = useState('');

  const save = (updated) => {
    db.savePricing(updated);
    setPricing(updated);
    toast('success', 'Pricing saved');
  };

  const setCategoryPrice = (cat, val) => {
    const n = parseFloat(val);
    if (isNaN(n) || n < 0) return;
    save({ ...pricing, byCategory: { ...pricing.byCategory, [cat]: n } });
  };

  const setParamPrice = (param, val) => {
    const n = parseFloat(val);
    if (isNaN(n)) return;
    save({ ...pricing, byParam: { ...pricing.byParam, [param]: n } });
  };

  const removeParam = (param) => {
    const byParam = { ...pricing.byParam };
    delete byParam[param];
    save({ ...pricing, byParam });
  };

  const addParam = () => {
    if (!newParam.name.trim()) { toast('error','Enter parameter name'); return; }
    const price = parseFloat(newParam.price);
    if (isNaN(price) || price < 0) { toast('error','Enter valid price'); return; }
    save({ ...pricing, byParam: { ...pricing.byParam, [newParam.name.trim()]: price } });
    setNewParam({ name:'', price:'' });
  };

  const setRushMult = (level, val) => {
    const n = parseFloat(val);
    if (isNaN(n)) return;
    save({ ...pricing, rushMultiplier: { ...pricing.rushMultiplier, [level]: n } });
  };

  const filteredParams = Object.entries(pricing.byParam)
    .filter(([k]) => !search || k.toLowerCase().includes(search.toLowerCase()))
    .sort((a,b) => a[0].localeCompare(b[0]));

  return (
    <div style={{display:'grid',gridTemplateColumns:'180px 1fr',gap:12,height:'100%'}}>
      {/* Section nav */}
      <div className="panel" style={{height:'fit-content'}}>
        <div className="panel-header">💰 Pricing Setup</div>
        <div style={{padding:6}}>
          {[
            ['category','📋 By Test Category'],
            ['param','🔬 By Parameter'],
            ['rush','⚡ Rush Multipliers'],
            ['misc','⚙️ Misc Charges'],
          ].map(([id,label]) => (
            <button key={id} onClick={() => setActiveSection(id)}
              className="btn" style={{
                width:'100%',justifyContent:'flex-start',marginBottom:4,
                background: activeSection===id ? '#1e3060' : '#f4f6fb',
                color: activeSection===id ? 'white' : '#1e3060',
                borderColor: activeSection===id ? '#1e3060' : '#cdd3e4',
              }}>{label}</button>
          ))}
          <div style={{borderTop:'1px solid #e0e4ec',marginTop:8,paddingTop:8}}>
            <button className="btn btn-sm" style={{width:'100%',color:'#888'}}
              onClick={() => { db.savePricing(db.getDefaultPricing()); setPricing(db.getPricing()); toast('success','Reset to defaults'); }}>
              ↺ Reset All
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="panel">
        {activeSection === 'category' && (
          <>
            <div className="panel-header">📋 Test Category Base Prices</div>
            <div className="panel-body">
              <div className="notice notice-info" style={{marginBottom:12}}>
                Used when a sample has no individual parameters assigned. Also used as the fallback base price.
              </div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))',gap:8}}>
                {Object.entries(pricing.byCategory).sort((a,b)=>a[0].localeCompare(b[0])).map(([cat,price]) => (
                  <div key={cat} style={{display:'flex',alignItems:'center',gap:8,padding:'6px 10px',background:'#f8f9ff',borderRadius:4,border:'1px solid #e8ecf8'}}>
                    <span style={{flex:1,fontSize:12}}>{cat}</span>
                    <span style={{fontSize:11,color:'#888'}}>$</span>
                    <input type="number" defaultValue={price} min="0" step="5"
                      onBlur={e => setCategoryPrice(cat, e.target.value)}
                      style={{width:80,border:'1px solid #cdd3e4',borderRadius:3,padding:'3px 6px',fontSize:12,textAlign:'right'}}/>
                  </div>
                ))}
              </div>
              {/* Add new category */}
              <div style={{marginTop:14,display:'flex',gap:8,alignItems:'center'}}>
                <input className="search-input" placeholder="New category name" id="new-cat-name" style={{flex:1}}/>
                <span style={{fontSize:11}}>$</span>
                <input type="number" id="new-cat-price" placeholder="0" min="0" step="5"
                  style={{width:80,border:'1px solid #cdd3e4',borderRadius:3,padding:'5px 6px',fontSize:12}}/>
                <button className="btn btn-primary" onClick={() => {
                  const name = document.getElementById('new-cat-name').value.trim();
                  const price = parseFloat(document.getElementById('new-cat-price').value);
                  if (!name || isNaN(price)) { toast('error','Fill in name and price'); return; }
                  save({ ...pricing, byCategory: { ...pricing.byCategory, [name]: price } });
                  document.getElementById('new-cat-name').value='';
                  document.getElementById('new-cat-price').value='';
                }}>＋ Add</button>
              </div>
            </div>
          </>
        )}

        {activeSection === 'param' && (
          <>
            <div className="panel-header">🔬 Per-Parameter Prices
              <span style={{marginLeft:'auto',fontSize:11,fontWeight:400}}>
                {Object.keys(pricing.byParam).length} parameters priced
              </span>
            </div>
            <div className="panel-body" style={{padding:10}}>
              <div className="notice notice-info" style={{marginBottom:10}}>
                When a sample has parameters assigned, the total is the <strong>sum of individual parameter prices</strong>.
                Parameters not listed here contribute $0 (category base price is used as fallback if total is $0).
              </div>
              {/* Add new */}
              <div style={{display:'flex',gap:6,marginBottom:10,flexWrap:'wrap'}}>
                <input className="search-input" placeholder="Parameter name" value={newParam.name}
                  onChange={e=>setNewParam(p=>({...p,name:e.target.value}))} style={{flex:2,minWidth:200}}/>
                <span style={{display:'flex',alignItems:'center',fontSize:11}}>$</span>
                <input type="number" placeholder="Price" value={newParam.price} min="0" step="5"
                  onChange={e=>setNewParam(p=>({...p,price:e.target.value}))}
                  style={{width:90,border:'1px solid #cdd3e4',borderRadius:4,padding:'5px 6px',fontSize:12}}/>
                <button className="btn btn-primary" onClick={addParam}>＋ Add</button>
              </div>
              <input className="search-input" placeholder="Search parameters..." value={search}
                onChange={e=>setSearch(e.target.value)} style={{width:'100%',marginBottom:8}}/>
              <div style={{overflowY:'auto',maxHeight:380,border:'1px solid #e0e4ec',borderRadius:4}}>
                <table className="data-table">
                  <thead><tr><th>Parameter</th><th style={{textAlign:'right'}}>Price ($)</th><th style={{width:80}}></th></tr></thead>
                  <tbody>
                    {filteredParams.map(([param, price]) => (
                      <tr key={param}>
                        <td style={{fontSize:11}}>
                          {editingParam===param
                            ? <input autoFocus defaultValue={param} id="edit-param-name"
                                style={{width:'100%',border:'1px solid #1e3060',borderRadius:3,padding:'2px 6px',fontSize:11}}/>
                            : param}
                        </td>
                        <td style={{textAlign:'right'}}>
                          <input type="number" defaultValue={price} min="0" step="5"
                            onBlur={e => setParamPrice(param, e.target.value)}
                            style={{width:80,border:'1px solid #cdd3e4',borderRadius:3,padding:'2px 6px',fontSize:11,textAlign:'right'}}/>
                        </td>
                        <td style={{display:'flex',gap:4}}>
                          <button className="btn btn-sm" style={{color:'#b83232',padding:'2px 6px'}}
                            onClick={() => removeParam(param)}>🗑</button>
                        </td>
                      </tr>
                    ))}
                    {filteredParams.length===0 && <tr><td colSpan={3} className="text-center text-muted" style={{padding:16}}>No parameters found</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {activeSection === 'rush' && (
          <>
            <div className="panel-header">⚡ Rush Multipliers</div>
            <div className="panel-body">
              <div className="notice notice-info" style={{marginBottom:12}}>
                Rush priority is set when receiving a sample (1=highest rush, 5=standard). The multiplier is applied to the total price.
              </div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:10}}>
                {Object.entries(pricing.rushMultiplier).sort((a,b)=>a[0]-b[0]).map(([level,mult]) => (
                  <div key={level} style={{background:'#f8f9ff',border:'1px solid #e0e4ec',borderRadius:6,padding:12,textAlign:'center'}}>
                    <div style={{fontSize:18,fontWeight:700,color: level<=2?'#b83232':level<=4?'#e09900':'#1a6b42'}}>
                      Rush {level}
                    </div>
                    <div style={{fontSize:11,color:'#888',marginBottom:8}}>
                      {level==1?'Same day':level==2?'Next day':level==3?'2-3 days':level==4?'4-5 days':'Standard'}
                    </div>
                    <input type="number" defaultValue={mult} step="0.05" min="1"
                      onBlur={e => setRushMult(level, e.target.value)}
                      style={{width:'100%',border:'1px solid #cdd3e4',borderRadius:3,padding:'4px',fontSize:14,textAlign:'center',fontWeight:700}}/>
                    <div style={{fontSize:10,color:'#888',marginTop:4}}>× multiplier</div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {activeSection === 'misc' && (
          <>
            <div className="panel-header">⚙️ Miscellaneous Charges</div>
            <div className="panel-body">
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,maxWidth:480}}>
                <div className="form-group"><label>Minimum Charge ($)</label>
                  <input type="number" defaultValue={pricing.minimumCharge} min="0" step="5"
                    onBlur={e => save({...pricing, minimumCharge: parseFloat(e.target.value)||0})}/>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CREATE / EDIT INVOICE MODAL
// ─────────────────────────────────────────────────────────────────────────────
function InvoiceModal({ invoice, samples, preselectedIds, onSave, onClose, toast }) {
  const isEdit = !!invoice?.num;
  const pricing = db.getPricing();



  const completedUnbilled = samples.filter(s => s.completed && (!s.billed || (isEdit && s.invoiceNum === invoice.num)));

  const [form, setForm] = useState(() => {
    if (isEdit) return {
      num: invoice.num,
      client: invoice.client,
      date: invoice.date,
      dueDate: invoice.dueDate || defaultDueDate(invoice.date),
      billingAddress: invoice.billingAddress || '',
      attention: invoice.attention || invoice.contact || '',
      terms: invoice.terms || 'Net 30',
      clientEmail: invoice.clientEmail || '',
      poNumber: invoice.poNumber || '',
      notes: invoice.notes || '',
      discount: invoice.discount || 0,
      status: invoice.status,
    };
    return {
      num: String(db.getCounters().nextInvoiceNum),
      client: '',
      date: todayStr(),
      dueDate: defaultDueDate(todayStr()),
      billingAddress: '',
      attention: '',
      terms: 'Net 30',
      clientEmail: '',
      poNumber: '',
      notes: '',
      discount: 0,
      status: 'Outstanding',
    };
  });

  const [selectedIds, setSelectedIds] = useState(new Set(
    isEdit ? (invoice.sampleIds || []) : (preselectedIds || [])
  ));
  const [samplePOs, setSamplePOs] = useState(() => ({ ...(invoice?.samplePOs || {}) }));
  const [clientSearch, setClientSearch] = useState('');
  const [sampleSearch, setSampleSearch] = useState('');

  const setF = (k,v) => setForm(p=>({...p,[k]:v}));
  const setSamplePO = (sampleId, value) => {
    const key = String(sampleId);
    setSamplePOs(previous => ({ ...previous, [key]:value }));
  };
  const toggleId = (id) => setSelectedIds(prev => { const n=new Set(prev); n.has(id)?n.delete(id):n.add(id); return n; });
  const toggleAll = () => {
    const visible = filteredSamples.map(s=>s.id);
    const allSelected = visible.every(id=>selectedIds.has(id));
    setSelectedIds(prev => {
      const n=new Set(prev);
      allSelected ? visible.forEach(id=>n.delete(id)) : visible.forEach(id=>n.add(id));
      return n;
    });
  };

  // Filter samples by client search and sample search
  const filteredSamples = useMemo(() => {
    let s = completedUnbilled;
    if (form.client) s = s.filter(x =>
      (x.client||x.source||'').toLowerCase().includes(form.client.toLowerCase()) ||
      selectedIds.has(x.id)
    );
    if (sampleSearch) {
      const q = sampleSearch.toLowerCase();
      s = s.filter(x => String(x.id).includes(q) || x.workOrder?.toLowerCase().includes(q) ||
        x.source?.toLowerCase().includes(q) || x.desc?.toLowerCase().includes(q));
    }
    return s;
  }, [completedUnbilled, form.client, sampleSearch, selectedIds]);

  // Unique clients for search dropdown, including clients found only on older invoices.
  const allClients = useMemo(() => {
    const set = new Set([
      ...samples.map(s => s.client || s.source),
      ...db.getInvoices().map(inv => inv.client),
      ...getClientProfiles().map(profile => profile.company),
    ].filter(Boolean));
    return [...set].sort();
  }, [samples]);

  const filteredClients = allClients.filter(c =>
    !clientSearch || c.toLowerCase().includes(clientSearch.toLowerCase())
  );

  const [showClientDrop, setShowClientDrop] = useState(false);

  const selectClientAndLoadProfile = (clientName) => {
    const normalized = String(clientName || '').trim().toLowerCase();
    const sharedProfile = getClientProfile(clientName);
    const invoiceProfile = db.getInvoices()
      .filter(inv => String(inv.client || '').trim().toLowerCase() === normalized)
      .sort((a, b) => String(b.num || '').localeCompare(String(a.num || ''), undefined, { numeric:true }))[0];
    const profile = sharedProfile || invoiceProfile;
    const billingContact = getBillingContact(clientName);

    setForm(previous => ({
      ...previous,
      client:clientName,
      ...(profile ? {
        billingAddress:sharedProfile?.billingAddress || invoiceProfile?.billingAddress || '',
        attention:billingContact?.name || invoiceProfile?.attention || invoiceProfile?.contact || '',
        terms:sharedProfile?.terms || invoiceProfile?.terms || 'Net 30',
        clientEmail:billingContact?.email || invoiceProfile?.clientEmail || '',
        poNumber:sharedProfile?.poNumber || invoiceProfile?.poNumber || '',
        notes:sharedProfile?.notes || invoiceProfile?.notes || '',
      } : {}),
    }));
    setClientSearch(clientName);
    setShowClientDrop(false);
    if (profile) toast('success', `Saved billing details loaded for ${clientName}`);
  };

  const subtotal = [...selectedIds].reduce((a,id) => {
    const s = samples.find(x=>x.id===id);
    if (!s) return a;
    return a + (s?.price || calcSamplePrice(s, pricing) || 0);
  }, 0);
  const discount = parseFloat(form.discount) || 0;
  const total = Math.max(subtotal - discount, 0);

  const handleSave = () => {
    if (!form.client) { toast('error','Client name required'); return; }
    if (selectedIds.size === 0) { toast('error','Select at least one sample'); return; }
    const selectedSamplePOs = [...selectedIds].reduce((result, id) => {
      const value = String(samplePOs[String(id)] || '').trim();
      if (value) result[String(id)] = value;
      return result;
    }, {});
    onSave({ form, selectedIds: [...selectedIds], samplePOs:selectedSamplePOs, subtotal, discount, total });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{width:900,maxHeight:'92vh'}} onClick={e=>e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEdit ? `✏️ Edit Invoice #${invoice.num}` : '＋ Create New Invoice'}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body" style={{display:'grid',gridTemplateColumns:'1fr 320px',gap:16}}>

          {/* Left - sample selection */}
          <div>
            <div className="form-section-title" style={{marginBottom:8}}>Select Completed Samples</div>
            <input className="search-input" placeholder="Work order (e.g. 26-0109), sample ID, source, description..."
              value={sampleSearch} onChange={e=>setSampleSearch(e.target.value)} style={{width:'100%',marginBottom:8}}/>
            <div style={{border:'1px solid #e0e4ec',borderRadius:4,overflow:'auto',maxHeight:420}}>
              <table className="data-table" style={{minWidth:760}}>
                <thead>
                  <tr>
                    <th style={{width:36}}>
                      <input type="checkbox"
                        checked={filteredSamples.length>0 && filteredSamples.every(s=>selectedIds.has(s.id))}
                        onChange={toggleAll} style={{accentColor:'white'}}/>
                    </th>
                    <th>Sample #</th>
                    <th>Work Order</th>
                    <th>Source / Client</th>
                    <th>Description</th>
                    <th>Type</th>
                    <th>Date</th>
                    <th style={{minWidth:100}}>Sample PO</th>
                    <th style={{textAlign:'right'}}>Price</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSamples.map(s => {
                    const price = s.price || calcSamplePrice(s, pricing);
                    return (
                      <tr key={s.id} className={`clickable ${selectedIds.has(s.id)?'selected':''}`}
                        onClick={()=>toggleId(s.id)}>
                        <td onClick={e=>e.stopPropagation()}>
                          <input type="checkbox" checked={selectedIds.has(s.id)}
                            onChange={()=>toggleId(s.id)} style={{accentColor:'#1e3060'}}/>
                        </td>
                        <td className="mono font-bold">{s.id}</td>
                        <td className="mono">{s.workOrder}</td>
                        <td style={{maxWidth:110,overflow:'hidden',textOverflow:'ellipsis'}}>{s.client||s.source}</td>
                        <td style={{maxWidth:130,overflow:'hidden',textOverflow:'ellipsis',fontSize:11}}>{s.desc}</td>
                        <td><span className={`badge badge-${s.testType?.toLowerCase()}`}>{s.testType}</span></td>
                        <td style={{fontSize:11}}>{s.received}</td>
                        <td onClick={e=>e.stopPropagation()}>
                          <input value={samplePOs[String(s.id)] || ''}
                            onFocus={()=>setSelectedIds(previous => new Set(previous).add(s.id))}
                            onChange={e=>setSamplePO(s.id, e.target.value)}
                            placeholder={form.poNumber ? `Default: ${form.poNumber}` : 'PO #'}
                            aria-label={`Purchase order for Sample ${s.id}`}
                            style={{width:96,border:'1px solid #9fb2cf',borderRadius:3,padding:'3px 5px',fontSize:10}}/>
                        </td>
                        <td style={{textAlign:'right',fontWeight:700}}>{fmt(price)}</td>
                      </tr>
                    );
                  })}
                  {filteredSamples.length===0 && (
                    <tr><td colSpan={9} className="text-center text-muted" style={{padding:20}}>
                      {form.client ? `No unbilled completed samples for "${form.client}"` : 'No unbilled completed samples'}
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div style={{marginTop:6,fontSize:11,color:'#888'}}>
              {selectedIds.size} sample{selectedIds.size!==1?'s':''} selected &nbsp;|&nbsp;
              {completedUnbilled.length} total unbilled
            </div>
          </div>

          {/* Right - invoice details */}
          <div style={{display:'flex',flexDirection:'column',gap:10}}>
            <div className="form-section-title">Invoice Details</div>

            {/* Client search */}
            <div className="form-group" style={{position:'relative'}}>
              <label>Client / Bill To *</label>
              <input value={form.client}
                onChange={e=>{setF('client',e.target.value);setClientSearch(e.target.value);setShowClientDrop(true);}}
                onFocus={()=>{setClientSearch(form.client);setShowClientDrop(true);}}
                onBlur={()=>setTimeout(()=>setShowClientDrop(false),150)}
                placeholder="Search or type client name..."
                style={{border:'1px solid #cdd3e4',borderRadius:4,padding:'5px 8px',fontSize:12,width:'100%',outline:'none'}}/>
              {showClientDrop && filteredClients.length>0 && (
                <div style={{position:'absolute',top:'100%',left:0,right:0,zIndex:300,
                  background:'white',border:'1px solid #cdd3e4',borderRadius:'0 0 4px 4px',
                  maxHeight:160,overflowY:'auto',boxShadow:'0 4px 12px rgba(0,0,0,0.12)'}}>
                  {filteredClients.map(c=>(
                    <div key={c} onMouseDown={()=>selectClientAndLoadProfile(c)}
                      style={{padding:'6px 10px',fontSize:12,cursor:'pointer',borderBottom:'1px solid #f4f4f4'}}
                      onMouseEnter={e=>e.currentTarget.style.background='#f0f4ff'}
                      onMouseLeave={e=>e.currentTarget.style.background='white'}>
                      {c}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div style={{fontSize:10,color:'#64748b',marginTop:-6}}>
              Selecting an existing client loads its most recently saved billing details.
            </div>

            <div className="form-group"><label>Invoice #</label>
              <input value={form.num} onChange={e=>setF('num',e.target.value)}
                style={{border:'1px solid #cdd3e4',borderRadius:4,padding:'5px 8px',fontSize:12}}/>
            </div>
            <div className="form-group"><label>Invoice Date</label>
              <input type="date" value={form.date} onChange={e=>setF('date',e.target.value)}
                style={{border:'1px solid #cdd3e4',borderRadius:4,padding:'5px 8px',fontSize:12}}/>
            </div>
            <div className="form-group"><label>Due Date</label>
              <input type="date" value={form.dueDate} onChange={e=>setF('dueDate',e.target.value)}
                style={{border:'1px solid #cdd3e4',borderRadius:4,padding:'5px 8px',fontSize:12}}/>
            </div>
            <div className="form-group"><label>Billing Address</label>
              <input value={form.billingAddress||''} onChange={e=>setF('billingAddress',e.target.value)}
                placeholder="Customer billing address"
                style={{border:'1px solid #cdd3e4',borderRadius:4,padding:'5px 8px',fontSize:12}}/>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 95px',gap:8}}>
              <div className="form-group"><label>Contact</label>
                <input value={form.attention||''} onChange={e=>setF('attention',e.target.value)}
                  placeholder="Contact name"
                  style={{border:'1px solid #cdd3e4',borderRadius:4,padding:'5px 8px',fontSize:12,width:'100%'}}/>
              </div>
              <div className="form-group"><label>Terms</label>
                <input value={form.terms||''} onChange={e=>setF('terms',e.target.value)}
                  placeholder="Net 30"
                  style={{border:'1px solid #cdd3e4',borderRadius:4,padding:'5px 8px',fontSize:12,width:'100%'}}/>
              </div>
            </div>
            <div className="form-group"><label>Client Email</label>
              <input type="email" value={form.clientEmail||''} onChange={e=>setF('clientEmail',e.target.value)}
                placeholder="client@company.com"
                style={{border:'1px solid #cdd3e4',borderRadius:4,padding:'5px 8px',fontSize:12}}/>
            </div>
            <div className="form-group"><label>Default PO / Job Number</label>
              <input value={form.poNumber||''} onChange={e=>setF('poNumber',e.target.value)}
                placeholder="Used only when a sample PO is blank"
                style={{border:'1px solid #cdd3e4',borderRadius:4,padding:'5px 8px',fontSize:12}}/>
            </div>
            <div className="form-group"><label>Notes</label>
              <input value={form.notes} onChange={e=>setF('notes',e.target.value)}
                placeholder="Additional notes..."
                style={{border:'1px solid #cdd3e4',borderRadius:4,padding:'5px 8px',fontSize:12}}/>
            </div>

            {/* Totals */}
            <div style={{background:'#f8f9ff',border:'1px solid #e0e4ec',borderRadius:6,padding:12,marginTop:4}}>
              <div style={{display:'flex',justifyContent:'space-between',fontSize:12,marginBottom:6}}>
                <span style={{color:'#666'}}>Subtotal ({selectedIds.size} samples)</span>
                <span style={{fontWeight:600}}>{fmt(subtotal)}</span>
              </div>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',fontSize:12,marginBottom:8}}>
                <span style={{color:'#666'}}>Discount ($)</span>
                <input type="number" value={form.discount} min="0" step="5"
                  onChange={e=>setF('discount',e.target.value)}
                  style={{width:90,border:'1px solid #cdd3e4',borderRadius:3,padding:'3px 6px',fontSize:12,textAlign:'right'}}/>
              </div>
              <div style={{display:'flex',justifyContent:'space-between',fontSize:16,fontWeight:700,color:'#1e3060',
                borderTop:'2px solid #1e3060',paddingTop:8}}>
                <span>TOTAL DUE</span>
                <span>{fmt(total)}</span>
              </div>
            </div>

            {/* Selected samples summary */}
            {selectedIds.size > 0 && (
              <div style={{background:'white',border:'1px solid #e0e4ec',borderRadius:4,padding:8,maxHeight:140,overflowY:'auto'}}>
                <div style={{fontSize:10,fontWeight:700,color:'#666',textTransform:'uppercase',marginBottom:4}}>Selected Samples</div>
                {[...selectedIds].map(id => {
                  const s = samples.find(x=>x.id===id);
                  if (!s) return null;
                  const price = s.price || calcSamplePrice(s, pricing);
                  return (
                    <div key={id} style={{display:'flex',justifyContent:'space-between',fontSize:11,padding:'2px 0',borderBottom:'1px solid #f5f5f5'}}>
                      <span className="mono">{s.id}</span>
                      <span style={{flex:1,marginLeft:8,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',color:'#666'}}>{s.desc||s.source}</span>
                      <span style={{fontWeight:600,marginLeft:6}}>{fmt(price)}</span>
                      <button onClick={()=>toggleId(id)}
                        style={{background:'none',border:'none',cursor:'pointer',color:'#b83232',marginLeft:4,fontSize:12}}>×</button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-primary" onClick={handleSave}>
            {isEdit ? '💾 Save Changes' : `💾 Create Invoice - ${fmt(total)}`}
          </button>
          <button className="btn" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN INVOICING COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
// STERLING_PAID_RECEIPT_R18: receipt-only wrapper; original invoice templates are unchanged.
function makePaidReceiptHTMLR18(inv, invSamples, getPrice, paidAmount) {
  if (inv.status !== 'Paid') throw new Error('Only paid invoices can have payment receipts');
  // Reuse exact existing invoice email HTML including logo, customer, sample lines and formatting.
  const original = buildInvoiceEmailHTML(inv, invSamples, getPrice, paidAmount);
  const esc = s => String(s ?? '').replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
  const panel = `<div style="border:2px solid #17663d;border-radius:5px;margin:12px 0 18px;padding:12px 16px;font-family:Arial,sans-serif;color:#17663d;">
    <div style="font-size:22px;font-weight:bold;letter-spacing:1px;">PAID - PAYMENT RECEIPT</div>
    <div style="font-size:12px;margin-top:4px;">Original Invoice #${esc(inv.num)} &nbsp; | &nbsp; ${inv.paidDate || inv.paymentDate ? `Payment date: ${esc(inv.paidDate || inv.paymentDate)}` : 'Payment date not recorded'}</div>
    <div style="font-size:14px;margin-top:7px;"><b>Payment received: ${fmt(paidAmount)} &nbsp; | &nbsp; Remaining balance: $0.00</b></div>
  </div>`;
  // Insertion only: never replace the existing invoice line-items, header, logo or footer.
  return original.replace(/<body([^>]*)>/i, (m) => `${m}${panel}`);
}
function printPaidReceiptR18(inv, samples, pricing={}) {
  if (inv.status !== 'Paid') return;
  const invSamples = (inv.sampleIds || []).map(id => samples.find(s=>String(s.id)===String(id))).filter(Boolean);
  const getPrice = s => s.price || calcSamplePrice(s, pricing) || 0;
  const total = Number.isFinite(Number(inv.amount)) ? Number(inv.amount) : Math.max(0, invSamples.reduce((a,s)=>a+getPrice(s),0)-(inv.discount||0));
  const html = makePaidReceiptHTMLR18(inv, invSamples, getPrice, total);
  const win = window.open('', '_blank');
  if (!win) { window.alert('Allow pop-ups to print the paid receipt'); return; }
  win.document.open();
  win.document.write(html);
  win.document.close();
  win.onload = () => win.print();
}

export default function Invoicing({ toast }) {
  const [activeTab, setActiveTab] = useState('unbilled');
  const [invoices, setInvoices] = useState(() => db.getInvoices());
  const [samples, setSamples] = useState(() => db.getSamples());
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [invSearch, setInvSearch] = useState('');
  const [unbilledSearch, setUnbilledSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedInv, setSelectedInv] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editInvoice, setEditInvoice] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailModal, setEmailModal] = useState(null); // {inv, email, poNumber, invoiceDate}
  const [priceDrafts, setPriceDrafts] = useState({});
  const [savingPriceIds, setSavingPriceIds] = useState(new Set());
  const [bulkPrice, setBulkPrice] = useState('');
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(null);

  const pricing = db.getPricing();

  const handleEmailInvoice = (inv, receiptModeR18=false) => {
    if (!inv) return;
    const client = inv.client || '';
    const savedEmail = inv.clientEmail ||
      invoices.find(i => i.client === client && i.clientEmail)?.clientEmail || '';
    // Get unique work orders from invoice samples
    const invSamples = (inv.sampleIds||[]).map(id=>samples.find(s=>s.id===id)).filter(Boolean);
    const workOrders = [...new Set(invSamples.map(s=>s.workOrder).filter(Boolean))];
    // Build woPOs map from saved data
    const woPOs = {};
    workOrders.forEach(wo => { woPOs[wo] = inv.woPOs?.[wo] || ''; });
    const invDate = inv.date || new Date().toISOString().split('T')[0];
    const invDue = inv.dueDate || new Date(new Date(invDate).getTime()+30*24*60*60*1000).toISOString().split('T')[0];
    setEmailModal({
      receiptModeR18,
      inv,
      email: savedEmail,
      woPOs,
      workOrders,
      invoiceDate: invDate,
      dueDate: invDue,
    });
  };

  const sendEmailInvoice = async () => {
    if (!emailModal) return;
    const { inv, email, woPOs, invoiceDate, receiptModeR18 } = emailModal;
    if (receiptModeR18 && inv.status !== 'Paid') { toast('error', 'Only paid invoices can have receipts'); return; }
    if (!email) { toast('error', 'Email address is required'); return; }
    const invSamples = (inv.sampleIds||[]).map(id=>samples.find(s=>s.id===id)).filter(Boolean);
    const getPrice = s => s.price || calcSamplePrice(s, pricing) || 0;
    const liveTotal = Math.max(invSamples.reduce((a,s)=>a+getPrice(s),0)-(inv.discount||0),0);
    const client = inv.client || '';
    // Save email and PO# back to invoice for next time. dateSent is set
    // only the FIRST time — this is the reference point reminder timing
    // is measured from (or from the most recent reminder, if any have
    // gone out since).
    const updatedInv = {
      ...inv, clientEmail: email, woPOs, date: invoiceDate, dueDate: emailModal.dueDate,
      dateSent: inv.dateSent || todayStr(),
    };
    const updatedList = invoices.map(i => i.num === inv.num ? updatedInv : i);
    db.updateInvoice(inv.num, {
      clientEmail: email,
      woPOs,
      date: invoiceDate,
      dueDate: emailModal.dueDate,
      dateSent: inv.dateSent || todayStr(),
    });
    setInvoices(updatedList);
    if (selectedInv?.num === inv.num) setSelectedInv(updatedInv);
    setSendingEmail(true);
    try {
      const receiptAmountR18 = Number.isFinite(Number(inv.amount)) ? Number(inv.amount) : liveTotal;
      const htmlContent = receiptModeR18
        ? makePaidReceiptHTMLR18(updatedInv, invSamples, getPrice, receiptAmountR18)
        : buildInvoiceEmailHTML(updatedInv, invSamples, getPrice, liveTotal);
      const res = await fetch(`${MAPI_URL}/send-mapi`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: email, cc: '',
          subject: receiptModeR18 ? `Payment Receipt for Invoice #${inv.num} - Sterling Analytical Laboratory` : `Invoice #${inv.num} from Sterling Analytical Laboratory`,
          body: receiptModeR18 ? `Dear ${client},\n\nThank you for your payment. Attached is the paid receipt for invoice #${inv.num}, showing payment received of ${fmt(receiptAmountR18)} and a remaining balance of $0.00.\n\nSterling Analytical, Inc.\n(413) 214-6541` : `Dear ${client},\n\nPlease find attached your invoice #${inv.num} for ${fmt(liveTotal)}.${woPOs && Object.keys(woPOs).length>0 ? '\n' + Object.entries(woPOs).filter(([,v])=>v).map(([wo,po])=>`WO ${wo} - PO/Job #: ${po}`).join('\n') : ''}\n\nSamples included:\n${invSamples.map(s=>`  - Sample ${s.id}: ${s.desc||s.source||''}`).join('\n')}\n\nThank you for your business.\n\nSterling Analytical, Inc.\n(413) 214-6541`,
          reportHTML: htmlContent,
          fileName: receiptModeR18 ? `Paid_Receipt_${inv.num}.pdf` : `Invoice_${inv.num}.pdf`,
          workOrders: emailModal.workOrders,
        }),
      });
      const data = await res.json();
      if (data.success) {
        toast('success', data.cocAttached
          ? '✅ Outlook opening with invoice and COC(s) attached'
          : '✅ Outlook opening with invoice attached');
        setEmailModal(null);
      }
      else toast('error', data.error || 'Failed to open Outlook');
    } catch(e) {
      toast('error', 'Email server not running. Start local server first.');
    } finally {
      setSendingEmail(false);
    }
  };

  // ── Payment reminder ────────────────────────────────────────────────────
  const [sendingReminder, setSendingReminder] = useState(false);
  const sendReminderEmail = async (inv) => {
    const email = inv.clientEmail;
    if (!email) { toast('error', 'No saved email for this client — send the original invoice first'); return; }
    const invSamples = (inv.sampleIds||[]).map(id=>samples.find(s=>s.id===id)).filter(Boolean);
    const getPrice = s => s.price || calcSamplePrice(s, pricing) || 0;
    const liveTotal = Math.max(invSamples.reduce((a,s)=>a+getPrice(s),0)-(inv.discount||0),0);
    const client = inv.client || '';
    const workOrders = [...new Set(invSamples.map(s=>s.workOrder).filter(Boolean))];
    const daysOut = daysSince(inv.dateSent);

    setSendingReminder(true);
    try {
      const htmlContent = buildInvoiceEmailHTML(inv, invSamples, getPrice, liveTotal, /*isReminder*/ true);
      const res = await fetch(`${MAPI_URL}/send-mapi`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: email, cc: '',
          subject: `Payment Reminder — Invoice #${inv.num} from Sterling Analytical Laboratory`,
          body: `Dear ${client},\n\nThis is a friendly reminder that invoice #${inv.num} for ${fmt(liveTotal)}, sent ${inv.dateSent||'previously'}${daysOut!=null?` (${daysOut} days ago)`:''}, remains outstanding.\n\nSamples included:\n${invSamples.map(s=>`  - Sample ${s.id}: ${s.desc||s.source||''}`).join('\n')}\n\nIf you've already sent payment, please disregard this notice. Otherwise, we'd appreciate your prompt attention.\n\nThank you for your business.\n\nSterling Analytical, Inc.\n(413) 214-6541`,
          reportHTML: htmlContent,
          fileName: `Invoice_${inv.num}_Reminder.pdf`,
          workOrders,
        }),
      });
      const data = await res.json();
      if (data.success) {
        const updatedInv = {
          ...inv,
          reminders: [...(inv.reminders||[]), { date: todayStr() }],
        };
        const updatedList = invoices.map(i => i.num === inv.num ? updatedInv : i);
        db.updateInvoice(inv.num, { reminders: updatedInv.reminders });
        setInvoices(updatedList);
        if (selectedInv?.num === inv.num) setSelectedInv(updatedInv);
        toast('success', '✅ Outlook opening with payment reminder attached');
      } else {
        toast('error', data.error || 'Failed to open Outlook');
      }
    } catch(e) {
      toast('error', 'Email server not running. Start local server first.');
    } finally {
      setSendingReminder(false);
    }
  };

  const refresh = () => {
    setInvoices(db.getInvoices());
    setSamples(db.getSamples());
  };

  // ── Unbilled samples ──────────────────────────────────────────────────────
  const unbilledSamples = useMemo(() => {
    let list = samples.filter(s => s.completed && !s.billed);
    if (unbilledSearch) {
      const q = unbilledSearch.toLowerCase();
      list = list.filter(s =>
        String(s.id).includes(q) ||
        s.workOrder?.toLowerCase().includes(q) ||
        (s.client||s.source||'').toLowerCase().includes(q) ||
        s.desc?.toLowerCase().includes(q) ||
        s.testType?.toLowerCase().includes(q)
      );
    }
    return list.sort((a,b) => b.id - a.id);
  }, [samples, unbilledSearch]);

  // Keep selected samples active even if the search box temporarily hides them.
  const selectedSamples = samples.filter(s => s.completed && !s.billed && selectedIds.has(s.id));
  const selectedTotal = selectedSamples.reduce((a,s) => a + (s.price||0), 0);
  const hasUnsavedSelectedPrices = selectedSamples.some(sample =>
    Object.prototype.hasOwnProperty.call(priceDrafts, String(sample.id))
  );
  const priceSaveInProgress = bulkSaving || savingPriceIds.size > 0;

  const toggleId = (id) => setSelectedIds(prev => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const toggleAll = () => {
    if (selectedIds.size === unbilledSamples.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(unbilledSamples.map(s => s.id)));
  };

  const setPriceDraft = (sampleId, value) => {
    const key = String(sampleId);
    setPriceDrafts(previous => ({ ...previous, [key]:value }));
  };

  const persistSamplePrice = async (sample, price) => {
    // Deliberately send only the existing billing field. Results, params,
    // completion, and report data are not part of this request.
    const saved = await db.updateSample(sample.id, { price:Number(price.toFixed(2)) });
    const record = saved?.record;
    if (!record) throw new Error('server did not return the saved sample');
    if (Number(record.price) !== Number(price.toFixed(2))) {
      throw new Error('server verification failed for the saved price');
    }
    if (!sameData(record.results || {}, sample.results || {})) {
      throw new Error('result verification failed; review the sample before invoicing');
    }
    if (!sameData(record.params || [], sample.params || [])) {
      throw new Error('parameter verification failed; review the sample before invoicing');
    }
    return record;
  };

  const saveSamplePrice = async sample => {
    const key = String(sample.id);
    const raw = Object.prototype.hasOwnProperty.call(priceDrafts, key)
      ? String(priceDrafts[key]).trim()
      : String(sample.price ?? '').trim();
    const price = Number(raw);
    if (raw === '' || !Number.isFinite(price) || price <= 0) {
      toast('error', `Enter a valid price greater than $0.00 for Sample ${sample.id}`);
      return;
    }
    if (savingPriceIds.has(key)) return;

    setSavingPriceIds(previous => new Set(previous).add(key));
    try {
      const record = await persistSamplePrice(sample, price);

      setSamples(previous => previous.map(item =>
        String(item.id) === key ? record : item
      ));
      setPriceDrafts(previous => {
        const next = { ...previous };
        delete next[key];
        return next;
      });
      toast('success', `Price confirmed saved for Sample ${sample.id}: ${fmt(price)}`);
    } catch (error) {
      toast('error', `Price was not confirmed for Sample ${sample.id}: ${error.message}`);
    } finally {
      setSavingPriceIds(previous => {
        const next = new Set(previous);
        next.delete(key);
        return next;
      });
    }
  };

  const saveBulkPrice = async () => {
    const raw = String(bulkPrice).trim();
    const price = Number(raw);
    const targets = selectedSamples.slice();
    if (targets.length === 0) {
      toast('error', 'Select one or more samples first');
      return;
    }
    if (raw === '' || !Number.isFinite(price) || price <= 0) {
      toast('error', 'Enter a valid same price greater than $0.00');
      return;
    }
    if (bulkSaving || savingPriceIds.size > 0) return;
    const confirmed = window.confirm(
      `Apply ${fmt(price)} to ${targets.length} selected sample${targets.length === 1 ? '' : 's'}? Only the price field will be updated.`
    );
    if (!confirmed) return;

    const targetKeys = new Set(targets.map(sample => String(sample.id)));
    setBulkSaving(true);
    setBulkProgress({ done:0, total:targets.length });
    setSavingPriceIds(previous => new Set([...previous, ...targetKeys]));

    const successes = [];
    const failures = [];
    let nextIndex = 0;
    const BULK_SAVE_CONCURRENCY = 4;
    const worker = async () => {
      while (true) {
        const index = nextIndex++;
        if (index >= targets.length) return;
        const sample = targets[index];
        try {
          const record = await persistSamplePrice(sample, price);
          successes.push({ sample, record });
        } catch (error) {
          failures.push({ sample, message:error.message });
        } finally {
          setBulkProgress(previous => ({
            done:Math.min((previous?.done || 0) + 1, targets.length),
            total:targets.length,
          }));
        }
      }
    };

    try {
      await Promise.all(
        Array.from({ length:Math.min(BULK_SAVE_CONCURRENCY, targets.length) }, () => worker())
      );
      const savedById = new Map(successes.map(({ record }) => [String(record.id), record]));
      setSamples(previous => previous.map(sample => savedById.get(String(sample.id)) || sample));
      setPriceDrafts(previous => {
        const next = { ...previous };
        successes.forEach(({ sample }) => delete next[String(sample.id)]);
        return next;
      });

      if (failures.length === 0) {
        toast('success', `${fmt(price)} confirmed saved for ${successes.length} selected sample${successes.length === 1 ? '' : 's'}`);
      } else {
        // Leave only failed samples selected so the user can retry them safely.
        setSelectedIds(new Set(failures.map(({ sample }) => sample.id)));
        const failedIds = failures.map(({ sample }) => sample.id).join(', ');
        toast('error', `Saved ${successes.length} of ${targets.length}. Not saved: Sample ${failedIds}. Those samples remain selected for retry.`);
      }
    } finally {
      setSavingPriceIds(previous => {
        const next = new Set(previous);
        targetKeys.forEach(key => next.delete(key));
        return next;
      });
      setBulkSaving(false);
      setBulkProgress(null);
    }
  };

  // Mark single sample as billed without invoice
  const markBilledManually = (s) => {
    db.updateSample(s.id, { billed: true, invoiceNum: 'MANUAL' });
    setSelectedIds(prev => { const n=new Set(prev); n.delete(s.id); return n; });
    refresh();
    toast('success', `Sample ${s.id} marked as billed`);
  };

  // ── Stats ────────────────────────────────────────────────────────────────
  const stats = useMemo(() => ({
    totalInvoices: invoices.filter(i=>i.status!=='Cancelled').length,
    outstanding: invoices.filter(i=>i.status==='Outstanding').reduce((a,i)=>{
      const ls=(i.sampleIds||[]).map(id=>samples.find(s=>s.id===id)).filter(Boolean);
      return a + Math.max(ls.reduce((x,s)=>x+(s.price||0),0)-(i.discount||0),0);
    },0),
    paid: invoices.filter(i=>i.status==='Paid').reduce((a,i)=>{
      const ls=(i.sampleIds||[]).map(id=>samples.find(s=>s.id===id)).filter(Boolean);
      return a + Math.max(ls.reduce((x,s)=>x+(s.price||0),0)-(i.discount||0),0);
    },0),
    unbilled: samples.filter(s=>s.completed&&!s.billed).length,
    unbilledAmt: samples.filter(s=>s.completed&&!s.billed).reduce((a,s)=>a+(s.price||0),0),
    remindersDue: invoices.filter(needsReminder).length,
  }), [invoices, samples]);

  // ── Invoice list ─────────────────────────────────────────────────────────
  const filteredInvoices = useMemo(() => {
    let list = [...invoices];
    if (filterStatus==='all') list=list.filter(i=>i.status!=='Cancelled');
    if (filterStatus==='cancelled') list=list.filter(i=>i.status==='Cancelled');
    if (filterStatus==='outstanding') list=list.filter(i=>i.status==='Outstanding');
    if (filterStatus==='paid') list=list.filter(i=>i.status==='Paid');
    if (invSearch) {
      const q=invSearch.toLowerCase();
      list=list.filter(i=>{
        if (i.num?.includes(q)||i.client?.toLowerCase().includes(q)) return true;
        const invS=(i.sampleIds||[]).map(id=>samples.find(s=>s.id===id)).filter(Boolean);
        return invS.some(s=>s.workOrder?.toLowerCase().includes(q)||String(s.id).includes(q));
      });
    }
    return list.sort((a,b)=>b.num.localeCompare(a.num));
  }, [invoices, filterStatus, invSearch, samples]);

  // ── Save invoice ─────────────────────────────────────────────────────────
  const handleSaveInvoice = async ({ form, selectedIds: ids, samplePOs, total, discount }) => {
    try {
      await saveClientProfile(form.client, {
        billingAddress:form.billingAddress,
        terms:form.terms,
        poNumber:form.poNumber,
        notes:form.notes,
        billingContact:{name:form.attention,email:form.clientEmail},
      });
    } catch (error) {
      toast('error', `Invoice was not saved because the company profile could not be confirmed: ${error.message}`);
      return;
    }
    const isEdit = !!editInvoice?.num;
    if (isEdit) {
      (editInvoice.sampleIds||[]).forEach(id => {
        if (!ids.includes(id)) db.updateSample(id,{billed:false,invoiceNum:''});
      });
      ids.forEach(id => db.updateSample(id,{billed:true,invoiceNum:form.num}));
      db.updateInvoice(form.num,{
        client:form.client, date:form.date, dueDate:form.dueDate,
        billingAddress:form.billingAddress, attention:form.attention, terms:form.terms,
        clientEmail:form.clientEmail, poNumber:form.poNumber,
        samplePOs, notes:form.notes, discount, amount:total, sampleIds:ids, status:form.status,
      });
      toast('success',`Invoice #${form.num} updated - ${fmt(total)}`);
    } else {
      let num;
      try {
        num = String(await db.nextInvoiceNum());
      } catch (error) {
        toast('error', `Could not reserve an invoice number: ${error.message}`);
        return;
      }
      ids.forEach(id => db.updateSample(id,{billed:true,invoiceNum:num}));
      db.addInvoice({ num, client:form.client, date:form.date, dueDate:form.dueDate,
        billingAddress:form.billingAddress, attention:form.attention, terms:form.terms,
        clientEmail:form.clientEmail, poNumber:form.poNumber,
        samplePOs, notes:form.notes, discount, amount:total, sampleIds:ids, status:'Outstanding' });
      toast('success',`Invoice #${num} created - ${fmt(total)}`);
    }
    setShowModal(false); setEditInvoice(null);
    setSelectedIds(new Set());
    refresh();
  };

  // STERLING_DELETE_ONLY_R17: cancel only; do not change invoice templates.
  const cancelDuplicateInvoiceR17 = (inv, e) => {
    e?.stopPropagation();
    if (!inv || inv.status !== 'Outstanding') {
      toast('error', 'Only outstanding invoices can be cancelled.');
      return;
    }
    const typed = window.prompt(`Cancel duplicate invoice #${inv.num} for ${inv.client}?\n\nThis keeps the number in the Cancelled audit log.\nType the exact invoice number to confirm:`);
    if (typed !== String(inv.num)) return;
    try {
      // Preserve original invoice in server storage; no invoice number is released.
      db.updateInvoice(inv.num, { status: 'Cancelled', cancelledAt: new Date().toISOString(), cancellationReason: 'Duplicate invoice' });
      // Only release samples that were actually tied to this invoice and have no other valid invoice.
      const otherInvoices = db.getInvoices().filter(i => i.status !== 'Cancelled' && String(i.num) !== String(inv.num));
      (inv.sampleIds || []).forEach(id => {
        const sample = db.getSamples().find(s => String(s.id) === String(id));
        if (!sample || String(sample.invoiceNum) !== String(inv.num)) return;
        const other = otherInvoices.find(i => (i.sampleIds || []).some(x => String(x) === String(id)));
        db.updateSample(id, other ? { billed: true, invoiceNum: other.num } : { billed: false, invoiceNum: '' });
      });
      refresh();
      if (selectedInv?.num === inv.num) setSelectedInv(null);
      toast('success', `Invoice #${inv.num} cancelled; number retained`);
    } catch (error) {
      refresh();
      toast('error', `Could not cancel invoice: ${error.message}`);
    }
  };

  const markPaid = (num, e) => {
    e && e.stopPropagation();
    if (db.getInvoices().some(i=>String(i.num)===String(num) && i.status==='Cancelled')) return;
    db.updateInvoice(num,{status:'Paid'});
    refresh();
    if(selectedInv?.num===num) setSelectedInv(p=>({...p,status:'Paid'}));
    toast('success',`Invoice #${num} marked as paid`);
  };

  const markUnpaid = (num, e) => {
    e && e.stopPropagation();
    db.updateInvoice(num,{status:'Outstanding'});
    refresh();
    if(selectedInv?.num===num) setSelectedInv(p=>({...p,status:'Outstanding'}));
    toast('success',`Invoice #${num} reverted to Outstanding`);
  };

  const tabStyle = (id) => ({
    background:'none',border:'none',
    color:activeTab===id?'#7ec8e3':'#99aac8',
    padding:'9px 16px',cursor:'pointer',fontSize:12,fontWeight:600,
    borderBottom:activeTab===id?'2px solid #7ec8e3':'2px solid transparent',
    display:'flex',alignItems:'center',gap:6,
  });

  return (
    <>
      {/* ── Tab bar ── */}
      <div style={{background:'#1e3060',display:'flex',gap:0,padding:'0 12px',flexShrink:0}}>
        <button style={tabStyle('unbilled')} onClick={()=>setActiveTab('unbilled')}>
          📋 Completed &amp; Unbilled
          <span style={{background:'rgba(255,255,255,0.15)',borderRadius:10,
            padding:'1px 7px',fontSize:10}}>{stats.unbilled}</span>
        </button>
        <button style={tabStyle('invoices')} onClick={()=>setActiveTab('invoices')}>
          🧾 Invoice Log
          <span style={{background:'rgba(255,255,255,0.15)',borderRadius:10,
            padding:'1px 7px',fontSize:10}}>{invoices.filter(i=>i.status!=='Cancelled').length}</span>
        </button>
        <button style={tabStyle('pricing')} onClick={()=>setActiveTab('pricing')}>
          🏷 Pricing Setup
        </button>
      </div>

      {/* ── UNBILLED TAB ── */}
      {activeTab==='unbilled' && (
        <>
          <div className="toolbar">
            <input className="search-input"
              placeholder="Search sample ID, work order, client, description..."
              value={unbilledSearch} onChange={e=>setUnbilledSearch(e.target.value)}
              disabled={priceSaveInProgress}
              style={{width:300}}/>
            {unbilledSearch && (
              <button className="btn btn-sm" onClick={()=>setUnbilledSearch('')}>✕</button>
            )}
            <div className="tb-sep"/>
            <span className="tb-label">Selected:</span>
            <span className="tb-value">{selectedIds.size}</span>
            <span className="tb-label" style={{marginLeft:4}}>
              ({fmt(selectedTotal)})
            </span>
            <div className="tb-sep"/>
            <span className="tb-label">Same price:</span>
            <span style={{fontSize:11,color:'#555'}}>$</span>
            <input type="number" min="0" step="0.01" inputMode="decimal"
              value={bulkPrice}
              placeholder="0.00"
              onFocus={e=>e.target.select()}
              onChange={e=>setBulkPrice(e.target.value)}
              onKeyDown={e=>{
                if (e.key === 'Enter') { e.preventDefault(); saveBulkPrice(); }
              }}
              disabled={priceSaveInProgress}
              aria-label="Same price for selected samples"
              style={{width:82,border:'1px solid #9fb2cf',borderRadius:3,padding:'5px 7px',fontSize:11,textAlign:'right',fontWeight:700,color:'#1a6b42'}}/>
            <button className="btn btn-primary"
              onClick={saveBulkPrice}
              disabled={selectedIds.size===0 || priceSaveInProgress}
              title="Save the same price to every selected sample; only the price field is updated">
              {bulkSaving && bulkProgress
                ? `⏳ Saving ${bulkProgress.done}/${bulkProgress.total}`
                : `💾 Apply & Save Price to Selected (${selectedIds.size})`}
            </button>
            <div className="tb-sep"/>
            <button className="btn btn-primary"
              disabled={selectedIds.size===0 || hasUnsavedSelectedPrices || priceSaveInProgress}
              title={priceSaveInProgress ? 'Wait for price saving to finish' : (hasUnsavedSelectedPrices ? 'Save the edited sample price(s) before creating the invoice' : '')}
              onClick={()=>{setEditInvoice(null);setShowModal(true);}}>
              🧾 Create Invoice from Selected ({selectedIds.size})
            </button>
            {selectedIds.size>0 && (
              <button className="btn" disabled={priceSaveInProgress}
                onClick={()=>setSelectedIds(new Set())}>
                Clear Selection
              </button>
            )}
          </div>

          <div className="content">
            {/* Stats */}
            <div className="stat-row" style={{marginBottom:12}}>
              <div className="stat-card">
                <div className="num" style={{color:'#e09900'}}>{stats.unbilled}</div>
                <div className="lbl">Unbilled Samples</div>
              </div>
              <div className="stat-card">
                <div className="num" style={{color:'#e09900'}}>{fmt(stats.unbilledAmt)}</div>
                <div className="lbl">Unbilled Value</div>
              </div>
              <div className="stat-card">
                <div className="num" style={{color:'#1e3060'}}>{selectedIds.size}</div>
                <div className="lbl">Selected</div>
              </div>
              <div className="stat-card">
                <div className="num" style={{color:'#1a6b42'}}>{fmt(selectedTotal)}</div>
                <div className="lbl">Selected Total</div>
              </div>
            </div>

            <div className="panel">
              <div className="panel-header">
                📋 Completed &amp; Unbilled Samples - {unbilledSamples.length} sample{unbilledSamples.length!==1?'s':''}
                <label style={{marginLeft:'auto',fontSize:11,fontWeight:400,
                  cursor:'pointer',display:'flex',alignItems:'center',gap:5}}>
                  <input type="checkbox"
                    checked={selectedIds.size===unbilledSamples.length&&unbilledSamples.length>0}
                    onChange={toggleAll} disabled={priceSaveInProgress}
                    style={{accentColor:'white'}}/>
                  Select all
                </label>
              </div>
              <div style={{overflowY:'auto',maxHeight:'calc(100vh - 320px)'}}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th style={{width:36}}></th>
                      <th>Sample #</th>
                      <th>Work Order</th>
                      <th>Client / Source</th>
                      <th>Description</th>
                      <th>Test Type</th>
                      <th>Date Received</th>
                      <th>Date Completed</th>
                      <th style={{textAlign:'center',minWidth:180}}>Price</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unbilledSamples.map(s => (
                      <tr key={s.id}
                        className={selectedIds.has(s.id)?'selected':''}
                        style={{cursor:'pointer'}}
                        onClick={()=>{ if (!priceSaveInProgress) toggleId(s.id); }}>
                        <td onClick={e=>e.stopPropagation()}>
                          <input type="checkbox"
                            checked={selectedIds.has(s.id)}
                            onChange={()=>toggleId(s.id)}
                            disabled={priceSaveInProgress}
                            style={{accentColor:'#1e3060'}}/>
                        </td>
                        <td><strong className="mono">{s.id}</strong></td>
                        <td className="mono">{s.workOrder}</td>
                        <td style={{maxWidth:140,overflow:'hidden',
                          textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                          {s.client||s.source}
                        </td>
                        <td style={{maxWidth:180,overflow:'hidden',
                          textOverflow:'ellipsis',whiteSpace:'nowrap',fontSize:11}}>
                          {s.desc}
                        </td>
                        <td>
                          <span className={`badge badge-${s.testType?.toLowerCase()}`}>
                            {s.testType}
                          </span>
                        </td>
                        <td style={{fontSize:11}}>{s.received}</td>
                        <td style={{fontSize:11}}>{s.dateCompleted}</td>
                        <td onClick={e=>e.stopPropagation()} style={{minWidth:180}}>
                          <div style={{display:'flex',alignItems:'center',justifyContent:'flex-end',gap:4}}>
                            <span style={{fontSize:11,color:'#555'}}>$</span>
                            <input type="number" min="0" step="0.01" inputMode="decimal"
                              value={Object.prototype.hasOwnProperty.call(priceDrafts, String(s.id))
                                ? priceDrafts[String(s.id)]
                                : String(s.price ?? '')}
                              placeholder="0.00"
                              onFocus={e=>e.target.select()}
                              onChange={e=>setPriceDraft(s.id, e.target.value)}
                              onKeyDown={e=>{
                                if (e.key === 'Enter') { e.preventDefault(); saveSamplePrice(s); }
                                if (e.key === 'Escape') {
                                  setPriceDrafts(previous => {
                                    const next = { ...previous };
                                    delete next[String(s.id)];
                                    return next;
                                  });
                                }
                              }}
                              disabled={priceSaveInProgress}
                              aria-label={`Price for Sample ${s.id}`}
                              style={{width:82,border:'1px solid #9fb2cf',borderRadius:3,padding:'4px 6px',fontSize:11,textAlign:'right',fontWeight:700,color:'#1a6b42'}}/>
                            <button className="btn btn-sm"
                              onClick={()=>saveSamplePrice(s)}
                              disabled={priceSaveInProgress || !Object.prototype.hasOwnProperty.call(priceDrafts, String(s.id))}
                              title={`Save price for Sample ${s.id}`}
                              style={{fontSize:10,padding:'3px 7px'}}>
                              {savingPriceIds.has(String(s.id)) ? '⏳' : '💾 Save'}
                            </button>
                          </div>
                        </td>
                        <td onClick={e=>e.stopPropagation()} style={{display:'flex',gap:4}}>
                          <button className="btn btn-sm"
                            title="Mark as billed without creating an invoice"
                            onClick={()=>markBilledManually(s)}
                            disabled={priceSaveInProgress}
                            style={{fontSize:10}}>
                            ☑ Mark Billed
                          </button>
                        </td>
                      </tr>
                    ))}
                    {unbilledSamples.length===0 && (
                      <tr><td colSpan={10} className="text-center text-muted"
                        style={{padding:30}}>
                        {unbilledSearch
                          ? 'No samples match your search'
                          : '🎉 All completed samples have been billed'}
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              {selectedIds.size>0 && (
                <div style={{padding:'10px 14px',background:'#f0f4ff',
                  borderTop:'1px solid #e0e4ec',display:'flex',
                  alignItems:'center',gap:12}}>
                  <span style={{fontSize:12,fontWeight:600,color:'#1e3060'}}>
                    {selectedIds.size} sample{selectedIds.size!==1?'s':''} selected -
                    Total: <strong>{fmt(selectedTotal)}</strong>
                  </span>
                  <button className="btn btn-primary"
                    disabled={hasUnsavedSelectedPrices || priceSaveInProgress}
                    title={priceSaveInProgress ? 'Wait for price saving to finish' : (hasUnsavedSelectedPrices ? 'Save the edited sample price(s) before creating the invoice' : '')}
                    onClick={()=>{setEditInvoice(null);setShowModal(true);}}>
                    🧾 Create Invoice
                  </button>
                  <button className="btn" disabled={priceSaveInProgress}
                    onClick={()=>setSelectedIds(new Set())}>
                    Clear Selection
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* ── INVOICES TAB ── */}
      {activeTab==='invoices' && (
        <>
          <div className="toolbar">
            <button className="btn btn-primary"
              onClick={()=>{setEditInvoice(null);setShowModal(true);}}>
              ＋ New Invoice
            </button>
            <div className="tb-sep"/>
            {[['all','All'],['outstanding','Outstanding'],['paid','Paid'],['cancelled','Cancelled']].map(([f,label])=>(
              <button key={f} className={`btn ${filterStatus===f?'btn-primary':''}`}
                onClick={()=>setFilterStatus(f)}>
                {label}
                {f==='outstanding' && (
                  <span style={{marginLeft:4,fontSize:10,
                    background:'rgba(255,255,255,0.2)',borderRadius:8,padding:'1px 5px'}}>
                    {invoices.filter(i=>i.status==='Outstanding').length}
                  </span>
                )}
              </button>
            ))}
            <div className="tb-sep"/>
            <input className="search-input"
              placeholder="Invoice #, client, work order, sample ID..."
              value={invSearch} onChange={e=>setInvSearch(e.target.value)}
              style={{width:260}}/>
            <div className="tb-sep"/>
            <span className="tb-label">Outstanding:</span>
            <span className="tb-value" style={{color:'#b83232'}}>{fmt(stats.outstanding)}</span>
            <span className="tb-label" style={{marginLeft:10}}>Paid:</span>
            <span className="tb-value" style={{color:'#1a6b42'}}>{fmt(stats.paid)}</span>
          </div>

          <div className="content">
            <div className="stat-row" style={{marginBottom:12}}>
              <div className="stat-card">
                <div className="num">{stats.totalInvoices}</div>
                <div className="lbl">Total Invoices</div>
              </div>
              <div className="stat-card">
                <div className="num" style={{color:'#b83232'}}>{fmt(stats.outstanding)}</div>
                <div className="lbl">Outstanding</div>
              </div>
              <div className="stat-card">
                <div className="num" style={{color:'#1a6b42'}}>{fmt(stats.paid)}</div>
                <div className="lbl">Total Paid</div>
              </div>
              <div className="stat-card">
                <div className="num" style={{color:'#e09900'}}>{stats.unbilled}</div>
                <div className="lbl">Unbilled Samples</div>
              </div>
              <div className="stat-card" style={{cursor:'pointer'}}
                onClick={()=>setFilterStatus('outstanding')}
                title="Outstanding invoices with no contact in 30+ days">
                <div className="num" style={{color: stats.remindersDue>0 ? '#d97706' : '#888'}}>
                  {stats.remindersDue}
                </div>
                <div className="lbl">⏰ Reminders Due</div>
              </div>
            </div>

            <div style={{display:'grid',gridTemplateColumns:'1fr 380px',gap:12}}>
              <div className="panel">
                <div className="panel-header">
                  🧾 Invoice Log ({filteredInvoices.length})
                </div>
                <div style={{overflowY:'auto',maxHeight:'calc(100vh - 280px)'}}>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Invoice #</th><th>Client</th><th>Samples</th>
                        <th style={{textAlign:'right'}}>Amount</th>
                        <th>Date</th><th>Due</th><th>Status</th><th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredInvoices.map(inv=>{
                        const ls=(inv.sampleIds||[]).map(id=>samples.find(s=>s.id===id)).filter(Boolean);
                        const liveAmt=Math.max(ls.reduce((a,s)=>a+(s.price||0),0)-(inv.discount||0),0);
                        const overdue=inv.status==='Outstanding'&&inv.dueDate&&new Date(inv.dueDate)<new Date();
                        return (
                          <tr key={inv.num}
                            className={`clickable ${selectedInv?.num===inv.num?'selected':''}`}
                            onClick={()=>setSelectedInv(inv)}>
                            <td><strong className="mono">#{inv.num}</strong></td>
                            <td style={{maxWidth:140,overflow:'hidden',
                              textOverflow:'ellipsis'}}>{inv.client}</td>
                            <td style={{textAlign:'center'}}>{inv.sampleIds?.length||0}</td>
                            <td style={{textAlign:'right',fontWeight:700}}>{fmt(liveAmt)}</td>
                            <td style={{fontSize:11}}>{inv.date}</td>
                            <td style={{fontSize:11,
                              color:overdue?'#b83232':'inherit',
                              fontWeight:overdue?700:400}}>
                              {inv.dueDate||'-'}{overdue?' ⚠️':''}
                            </td>
                            <td>
                              <span className={`badge ${inv.status==='Paid'?'badge-paid':'badge-outstanding'}`}>
                                {inv.status}
                              </span>
                              {needsReminder(inv) && (
                                <span title="30+ days since last contact — reminder due"
                                  style={{marginLeft:5,fontSize:10,fontWeight:700,color:'#d97706'}}>
                                  ⏰ Due
                                </span>
                              )}
                            </td>
                            <td style={{display:'flex',gap:3}}>
                              <button className="btn btn-sm"
                                onClick={e=>{e.stopPropagation();setEditInvoice(inv);setShowModal(true);}}>✏️</button>
                              <button className="btn btn-sm"
                                onClick={e=>{e.stopPropagation();printInvoiceHTML(inv,samples,pricing);}}>🖨</button>
                              {inv.status==='Paid' && <button className="btn btn-sm" title="Print paid receipt (same invoice layout)" onClick={e=>{e.stopPropagation();printPaidReceiptR18(inv,samples,pricing);}}>🧾 Receipt</button>}
                              {inv.status==='Paid' && <button className="btn btn-sm" title="Email paid receipt as PDF via Outlook" onClick={e=>{e.stopPropagation();handleEmailInvoice(inv,true);}}>✉️ Receipt</button>}
                                <button className="btn btn-sm"
                                  style={{background:'#0078d4',color:'white',borderColor:'#0078d4',padding:'3px 7px'}}
                                  onClick={e=>{e.stopPropagation();handleEmailInvoice(inv);}}>✉️</button>
                              {inv.status==='Outstanding' && inv.dateSent && (
                                <button className="btn btn-sm"
                                  title="Send payment reminder"
                                  style={needsReminder(inv)
                                    ? {background:'#d97706',color:'white',borderColor:'#d97706',padding:'3px 7px'}
                                    : {padding:'3px 7px'}}
                                  onClick={e=>{e.stopPropagation();sendReminderEmail(inv);}}>⏰</button>
                              )}
                              {inv.status==='Outstanding'&&(
                                <button className="btn btn-sm btn-success"
                                  onClick={e=>markPaid(inv.num,e)}>✓ Paid</button>
                              )}
                              {inv.status==='Outstanding' && (
                                <button className="btn btn-sm" title="Cancel duplicate invoice (preserve number)"
                                  style={{color:'#b83232',borderColor:'#b83232'}}
                                  onClick={e=>cancelDuplicateInvoiceR17(inv,e)}>🗑 Delete</button>
                              )}
                              {inv.status==='Paid'&&(
                                <button className="btn btn-sm"
                                  title="Undo — revert to Outstanding"
                                  style={{padding:'3px 7px',color:'#888'}}
                                  onClick={e=>markUnpaid(inv.num,e)}>↩️</button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                      {filteredInvoices.length===0&&(
                        <tr><td colSpan={8} className="text-center text-muted"
                          style={{padding:24}}>No invoices found</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Invoice preview */}
              <div className="panel">
                <div className="panel-header">
                  🧾 {selectedInv?`Invoice #${selectedInv.num}`:'Invoice Preview'}
                </div>
                <div className="panel-body"
                  style={{overflowY:'auto',maxHeight:'calc(100vh - 300px)'}}>
                  {!selectedInv ? (
                    <div className="text-muted text-sm"
                      style={{textAlign:'center',padding:24}}>
                      Click an invoice to preview
                    </div>
                  ) : (()=>{
                    const invS=(selectedInv.sampleIds||[]).map(id=>samples.find(s=>s.id===id)).filter(Boolean);
                    const getP = s => s.price || calcSamplePrice(s, pricing) || 0;
                    const getSamplePO = s => selectedInv.samplePOs?.[String(s.id)] ||
                      selectedInv.woPOs?.[s.workOrder] || selectedInv.poNumber || '';
                    const liveTotal=invS.reduce((a,s)=>a+getP(s),0);
                    const disc=selectedInv.discount||0;
                    const finalTotal=Math.max(liveTotal-disc,0);
                    return (
                      <div style={{fontSize:11}}>
                        <div style={{display:'flex',justifyContent:'space-between',
                          marginBottom:10,paddingBottom:8,borderBottom:'2px solid #1e3060'}}>
                          <div>
                            <img src={LOGO_BASE64} alt="Sterling Analytical"
                              style={{height:42,maxWidth:150,objectFit:'contain'}}/>
                            <div style={{fontSize:10,color:'#666'}}>West Springfield, MA</div>
                          </div>
                          <div style={{textAlign:'right'}}>
                            <div style={{fontSize:18,fontWeight:700,color:'#1e3060'}}>INVOICE</div>
                            <div style={{fontSize:12,fontWeight:700,color:'#1e3060'}}>#{selectedInv.num}</div>
                            <div style={{fontSize:10,color:'#888'}}>{selectedInv.date}</div>
                            <span className={`badge ${selectedInv.status==='Paid'?'badge-paid':'badge-outstanding'}`}>
                              {selectedInv.status}
                            </span>
                          </div>
                        </div>
                        <div style={{background:'#f0f4ff',padding:'6px 8px',
                          borderRadius:3,marginBottom:10,fontSize:11}}>
                          <strong>Bill To:</strong> {selectedInv.client}
                          {selectedInv.attention&&(
                            <div style={{color:'#444',marginTop:2}}>{selectedInv.attention}</div>
                          )}
                          {selectedInv.poNumber&&(
                            <div style={{color:'#666',marginTop:2}}>
                              <strong>PO / Job #:</strong> {selectedInv.poNumber}
                            </div>
                          )}
                          {selectedInv.notes&&(
                            <div style={{color:'#666',marginTop:2}}>{selectedInv.notes}</div>
                          )}
                        </div>
                        <table style={{width:'100%',borderCollapse:'collapse',
                          fontSize:10,marginBottom:8}}>
                          <thead>
                            <tr style={{borderBottom:'2px solid #1e3060',background:'#f8f9ff'}}>
                              <th style={{padding:'4px 6px',textAlign:'left'}}>Sample</th>
                              <th style={{padding:'4px 6px',textAlign:'left'}}>PO</th>
                              <th style={{padding:'4px 6px',textAlign:'left'}}>Description</th>
                              <th style={{padding:'4px 6px',textAlign:'right'}}>Price</th>
                            </tr>
                          </thead>
                          <tbody>
                            {invS.map(s=>(
                              <tr key={s.id} style={{borderBottom:'1px solid #eef0f8'}}>
                                <td style={{padding:'3px 6px'}} className="mono">{s.id}</td>
                                <td style={{padding:'3px 6px'}} className="mono">{getSamplePO(s)}</td>
                                <td style={{padding:'3px 6px',maxWidth:140,
                                  overflow:'hidden',textOverflow:'ellipsis'}}>
                                  {s.desc||s.source}
                                </td>
                                <td style={{padding:'3px 6px',textAlign:'right',fontWeight:600}}>
                                  {fmt(getP(s))}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            {disc>0&&(
                              <tr style={{background:'#fff8f8'}}>
                                <td colSpan={3} style={{padding:'3px 6px',
                                  textAlign:'right',color:'#b83232'}}>Discount:</td>
                                <td style={{padding:'3px 6px',textAlign:'right',
                                  color:'#b83232'}}>-{fmt(disc)}</td>
                              </tr>
                            )}
                            <tr style={{background:'#f0f4ff',fontWeight:700}}>
                              <td colSpan={3} style={{padding:'5px 6px',
                                textAlign:'right',fontSize:12}}>TOTAL:</td>
                              <td style={{padding:'5px 6px',textAlign:'right',
                                fontSize:14}}>{fmt(finalTotal)}</td>
                            </tr>
                          </tfoot>
                        </table>
                        <div style={{display:'flex',gap:6,marginTop:8,flexWrap:'wrap'}}>
                          <button className="btn btn-primary btn-sm"
                            onClick={()=>printInvoiceHTML(selectedInv,samples,pricing)}>
                            🖨 Print
                          </button>
                          {selectedInv.status==='Paid' && <button className="btn btn-sm" onClick={()=>printPaidReceiptR18(selectedInv,samples,pricing)}>🧾 Print Receipt</button>}
                          {selectedInv.status==='Paid' && <button className="btn btn-sm" onClick={()=>handleEmailInvoice(selectedInv,true)}>✉️ Email Receipt</button>}
                          <button className="btn btn-sm"
                            style={{background:'#0078d4',color:'white',borderColor:'#0078d4'}}
                            disabled={sendingEmail}
                            onClick={()=>handleEmailInvoice(selectedInv)}>
                            {sendingEmail?'⏳':'✉️'} Email
                          </button>
                          {selectedInv.status==='Outstanding' && selectedInv.dateSent && (
                            <button className="btn btn-sm"
                              style={needsReminder(selectedInv)
                                ? {background:'#d97706',color:'white',borderColor:'#d97706',fontWeight:700}
                                : {background:'white',color:'#d97706',borderColor:'#e0c060'}}
                              disabled={sendingReminder}
                              title={needsReminder(selectedInv)
                                ? `Due — ${daysSince(selectedInv.reminders?.length ? selectedInv.reminders[selectedInv.reminders.length-1].date : selectedInv.dateSent)} days since last contact`
                                : 'Send a payment reminder now (not yet 30 days since last contact)'}
                              onClick={()=>sendReminderEmail(selectedInv)}>
                              {sendingReminder?'⏳':'⏰'} Send Reminder
                              {selectedInv.reminders?.length ? ` (${selectedInv.reminders.length} sent)` : ''}
                            </button>
                          )}
                          <button className="btn btn-sm"
                            onClick={()=>{setEditInvoice(selectedInv);setShowModal(true);}}>
                            ✏️ Edit
                          </button>
                          {selectedInv.status==='Outstanding'&&(
                            <button className="btn btn-sm btn-success"
                              onClick={e=>markPaid(selectedInv.num,e)}>
                              ✅ Mark Paid
                            </button>
                          )}
                          {selectedInv.status==='Paid'&&(
                            <button className="btn btn-sm"
                              style={{color:'#888'}}
                              title="Undo — revert to Outstanding"
                              onClick={e=>markUnpaid(selectedInv.num,e)}>
                              ↩️ Mark Unpaid
                            </button>
                          )}
                        </div>
                        {selectedInv.dateSent && (
                          <div style={{fontSize:11,color:'#888',marginTop:8}}>
                            Sent {selectedInv.dateSent}
                            {selectedInv.reminders?.length > 0 && (
                              <> &nbsp;·&nbsp; Last reminder {selectedInv.reminders[selectedInv.reminders.length-1].date} ({selectedInv.reminders.length} total)</>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── PRICING TAB ── */}
      {activeTab==='pricing' && (
        <div className="content"><PricingTab toast={toast}/></div>
      )}

      {/* ── Invoice modal ── */}
      {showModal && (
        <InvoiceModal
          invoice={editInvoice}
          samples={samples}
          preselectedIds={selectedIds.size>0 ? [...selectedIds] : null}
          onSave={handleSaveInvoice}
          onClose={()=>{setShowModal(false);setEditInvoice(null);}}
          toast={toast}
        />
      )}

      {/* Email Invoice Modal */}
      {emailModal && (
        <div className="modal-overlay" onClick={()=>setEmailModal(null)}>
          <div className="modal" style={{width:480}} onClick={e=>e.stopPropagation()}>
            <div className="modal-header" style={{background:'#0078d4'}}>
              <h2>{emailModal.receiptModeR18 ? '✉️ Email Paid Receipt #' : '✉️ Email Invoice #'}{emailModal.inv.num}</h2>
              <button className="modal-close" onClick={()=>setEmailModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{background:'#f0f6ff',border:'1px solid #bfdbfe',borderRadius:4,
                padding:'8px 12px',fontSize:11,color:'#1e40af',marginBottom:14}}>
                Sending to: <strong>{emailModal.inv.client}</strong>
                &nbsp;·&nbsp; Total: <strong>{fmt(Math.max(
                  (emailModal.inv.sampleIds||[]).map(id=>samples.find(s=>s.id===id)).filter(Boolean)
                    .reduce((a,s)=>a+(s.price||calcSamplePrice(s,pricing)||0),0)
                  -(emailModal.inv.discount||0),0
                ))}</strong>
              </div>
              <div style={{display:'flex',flexDirection:'column',gap:12}}>
                <div className="form-group">
                  <label>Recipient Email *</label>
                  <input type="email"
                    value={emailModal.email}
                    onChange={e=>setEmailModal(p=>({...p,email:e.target.value}))}
                    placeholder="client@company.com"
                    style={{border:'1px solid #cdd3e4',borderRadius:4,padding:'7px 10px',
                      fontSize:13,outline:'none',width:'100%'}}
                    autoFocus/>
                  <div style={{fontSize:10,color:'#888',marginTop:2}}>
                    Saved automatically for next time
                  </div>
                </div>
                <div className="form-group">
                  <label>PO / Job Number {emailModal.workOrders.length > 1 ? '(per Work Order)' : ''}</label>
                  {emailModal.workOrders.length === 0 ? (
                    <input disabled placeholder="No work orders found"
                      style={{border:'1px solid #cdd3e4',borderRadius:4,padding:'7px 10px',fontSize:13,width:'100%',background:'#f5f5f5'}}/>
                  ) : emailModal.workOrders.map(wo => (
                    <div key={wo} style={{display:'flex',alignItems:'center',gap:8,marginBottom:6}}>
                      <span style={{fontSize:11,fontWeight:600,color:'#555',minWidth:80,fontFamily:'monospace'}}>{wo}</span>
                      <input
                        value={emailModal.woPOs[wo]||''}
                        onChange={e=>setEmailModal(p=>({...p,woPOs:{...p.woPOs,[wo]:e.target.value}}))}
                        placeholder="PO # or Job Reference"
                        style={{border:'1px solid #cdd3e4',borderRadius:4,padding:'6px 10px',
                          fontSize:12,outline:'none',flex:1}}/>
                    </div>
                  ))}
                  <div style={{fontSize:10,color:'#888',marginTop:2}}>
                    Will appear on the invoice PDF next to each work order
                  </div>
                </div>
                <div style={{display:'flex',gap:8}}>
                  <div className="form-group" style={{flex:1}}>
                    <label>Invoice Date</label>
                    <input type="date"
                      value={emailModal.invoiceDate}
                      onChange={e=>{
                        const d=e.target.value;
                        const due=new Date(new Date(d).getTime()+30*24*60*60*1000).toISOString().split('T')[0];
                        setEmailModal(p=>({...p,invoiceDate:d,dueDate:due}));
                      }}
                      style={{border:'1px solid #cdd3e4',borderRadius:4,padding:'7px 10px',fontSize:12,outline:'none',width:'100%'}}/>
                  </div>
                  <div className="form-group" style={{flex:1}}>
                    <label>Due Date <span style={{fontSize:10,color:'#16a34a',fontWeight:400}}>auto +30 days</span></label>
                    <input type="date"
                      value={emailModal.dueDate||''}
                      onChange={e=>setEmailModal(p=>({...p,dueDate:e.target.value}))}
                      style={{border:'1px solid #cdd3e4',borderRadius:4,padding:'7px 10px',fontSize:12,outline:'none',width:'100%'}}/>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                style={{background:sendingEmail?'#666':'#0078d4',color:'white',border:'none',
                  borderRadius:4,padding:'8px 22px',cursor:sendingEmail?'not-allowed':'pointer',
                  fontSize:13,fontWeight:700,display:'flex',alignItems:'center',gap:6}}
                onClick={sendEmailInvoice} disabled={sendingEmail}>
                {sendingEmail ? '⏳ Opening Outlook...' : '✉️ Send via Outlook (PDF attached)'}
              </button>
              <button className="btn" onClick={()=>setEmailModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
